import React, { useCallback, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import '../styles/auth.css';
import { getApiUrl, getCsrfToken } from '../utils/apiHelper';
import TwoFactorModal from './TwoFactorModal';
import { useUser } from '../contexts/UserContext';

export default function Login() {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [twoFaOpen, setTwoFaOpen] = useState(false);
  const [twoFaEmail, setTwoFaEmail] = useState('');
  const navigate = useNavigate();
  const { refreshUserData } = useUser();

  const handleLoginSuccess = useCallback(async () => {
    localStorage.removeItem('user');
    await refreshUserData();
    navigate('/');
  }, [navigate, refreshUserData]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError(''); // Clear error when user types
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Get CSRF token first
      const csrfToken = await getCsrfToken();

      const response = await fetch(getApiUrl('/api/internal/auth/login/2fa/initiate/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include', // Include cookies for session
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        // If backend returned full login payload, we are done.
        if (data.user) {
          await handleLoginSuccess();
          return;
        }

        // Otherwise, 2FA is pending: show code entry modal.
        setTwoFaEmail(formData.email);
        setTwoFaOpen(true);
      } else {
        setError(data.message || 'Login failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h2>Login</h2>
          <p>Sign in to your account</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && <div className="error-message">{error}</div>}
          
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>

          <div style={{ textAlign: 'right', marginTop: '-10px', marginBottom: '10px' }}>
            <Link to="/forgot-password" className="auth-link" style={{ fontSize: '14px' }}>
              Forgot Password?
            </Link>
          </div>

          <button 
            type="submit" 
            className="auth-button"
            disabled={loading}
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Don't have an account?{' '}
            <a href="/signup" className="auth-link">Sign up</a>
          </p>
        </div>
      </div>

      <TwoFactorModal
        isOpen={twoFaOpen}
        email={twoFaEmail}
        onClose={() => {
          setTwoFaOpen(false);
          setTwoFaEmail('');
        }}
        onVerified={(data) => {
          setTwoFaOpen(false);
          setTwoFaEmail('');
          if (data?.success && data?.user) {
            handleLoginSuccess();
          } else {
            setError(data?.message || 'Login failed');
          }
        }}
      />
    </div>
  );
}
